import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';
import { Transaction } from '@/types/pos/transaction';
import { hasFullAccessToMemberNIC, formatNIC, capitalizeFirstLetter } from '@/lib/helper';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

export async function salesReportTemplate(
  options: BasePDFOptions<Transaction[]>
): Promise<void> {
  const {
    data,
    filename = 'sales-report.pdf',
    title = 'Sales Report',
    orientation = 'portrait',
    summary
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const isOwner = hasFullAccessToMemberNIC();
  // Use smaller margins when owner to maximize table space
  const margin = isOwner ? 5 : 10;
  let yPosition = margin;

  // Header
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 10);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formattedTime = currentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  pdf.text(`Generated: ${formattedDate} at ${formattedTime}`, margin, 17);

  const totalRecordsText = `Total Records: ${summary?.totalCount || data.length}`;
  const filteredRecordsText = `Filtered: ${summary?.filteredCount || data.length}`;
  pdf.text(totalRecordsText, pageWidth - margin, 10, { align: 'right' });
  pdf.text(filteredRecordsText, pageWidth - margin, 17, { align: 'right' });

  yPosition = 30;

  // Compact Summary Section
  if (summary) {
    const summaryStartY = yPosition;
    let tempY = yPosition + 4;
    
    // Calculate required height first
    tempY += 6; // Financial Summary title
    
    // Financial cards - only show if value > 0
    const financialCards = [
      { label: 'TOTAL SALES', value: (summary.totalSales as number) || 0, color: [34, 197, 94] as [number, number, number] },
      { label: 'SUBTOTAL', value: (summary.netAmount as number) || 0, color: [59, 130, 246] as [number, number, number] },
      { label: 'TOTAL TAX', value: (summary.totalTax as number) || 0, color: [99, 102, 241] as [number, number, number] },
      { label: 'TOTAL REFUNDS', value: (summary.totalRefunds as number) || 0, color: [239, 68, 68] as [number, number, number] },
    ].filter(card => card.value > 0);

    if (financialCards.length > 0) {
      tempY += 12 + 4; // Card height + gap
    }

    // Payment Methods Summary - only if payment_methods exists in API response
    const paymentMethods = summary.payment_methods as Record<string, number> | undefined;
    const filteredPaymentMethods = paymentMethods
      ? Object.entries(paymentMethods)
          .filter(([method, amount]) => amount > 0)
          .sort(([, a], [, b]) => b - a)
      : [];

    if (filteredPaymentMethods.length > 0) {
      tempY += 5; // Payment Methods title
      const colsPerRow = orientation === 'landscape' ? 6 : 4;
      const cardHeight = 8;
      const cardGap = 2;
      const rowsNeeded = Math.ceil(filteredPaymentMethods.length / colsPerRow);
      tempY += (rowsNeeded * cardHeight) + ((rowsNeeded - 1) * cardGap) + 4;
    } else {
      tempY += 2;
    }

    // Draw background
    const summaryHeight = tempY - summaryStartY + 2;
    pdf.setFillColor(248, 250, 252);
    pdf.rect(0, summaryStartY, pageWidth, summaryHeight, 'F');
    
    // Draw content
    yPosition = summaryStartY + 4;
    pdf.setTextColor(15, 23, 42);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Financial Summary', margin, yPosition);
    yPosition += 6;

    if (financialCards.length > 0) {
      const cardWidth = (pageWidth - (margin * 2) - ((financialCards.length - 1) * 2)) / financialCards.length;
      const cardHeight = 12;
      const cardGap = 2;

      financialCards.forEach((card, index) => {
        const cardX = margin + (index * (cardWidth + cardGap));
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.2);
        pdf.roundedRect(cardX, yPosition, cardWidth, cardHeight, 1.5, 1.5, 'FD');

        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'bold');
        pdf.text(card.label, cardX + 2, yPosition + 4);

        pdf.setTextColor(card.color[0], card.color[1], card.color[2]);
        pdf.setFontSize(orientation === 'landscape' ? 10 : 9);
        pdf.setFont('helvetica', 'bold');
        const valueText = `PKR ${formatCurrency(card.value)}`;
        const truncatedValue = valueText.length > 18 ? truncateText(valueText, 18) : valueText;
        pdf.text(truncatedValue, cardX + 2, yPosition + 9);
      });
    }

    yPosition += 12 + 4;

    if (filteredPaymentMethods.length > 0) {
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Payment Methods', margin, yPosition);
      yPosition += 5;

      const colsPerRow = orientation === 'landscape' ? 6 : 4;
      const cardWidth = (pageWidth - (margin * 2) - ((colsPerRow - 1) * 2)) / colsPerRow;
      const cardHeight = 8;
      const cardGap = 2;
      let currentCol = 0;
      let startY = yPosition;

      filteredPaymentMethods.forEach(([method, amount], index) => {
        if (currentCol >= colsPerRow) {
          currentCol = 0;
          yPosition += cardHeight + cardGap;
          startY = yPosition;
        }

        const cardX = margin + (currentCol * (cardWidth + cardGap));
        const paymentColors = [
          [147, 51, 234], // purple
          [249, 115, 22], // orange
          [20, 184, 166], // teal
          [236, 72, 153], // pink
          [59, 130, 246], // blue
          [99, 102, 241], // indigo
        ];
        const color = paymentColors[index % paymentColors.length] as [number, number, number];

        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.2);
        pdf.roundedRect(cardX, startY, cardWidth, cardHeight, 1.5, 1.5, 'FD');

        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(5.5);
        pdf.setFont('helvetica', 'bold');
        const methodText = truncateText(capitalizeFirstLetter(method), 50);
        pdf.text(methodText, cardX + 1.5, startY + 3.5);

        pdf.setTextColor(color[0], color[1], color[2]);
        pdf.setFontSize(orientation === 'landscape' ? 8 : 7);
        pdf.setFont('helvetica', 'bold');
        const valueText = `PKR ${formatCurrency(amount)}`;
        const truncatedValue = truncateText(valueText, 50);
        pdf.text(truncatedValue, cardX + 1.5, startY + 6.5);

        currentCol++;
      });

      yPosition = startY + cardHeight + 4;
    } else {
      yPosition += 2;
    }
  }

  // Table header
  pdf.setTextColor(15, 23, 42);
  yPosition += 7;

  const headers = isOwner
    ? ['TXN #', 'SRB #', 'Member', 'CNIC/NTN', 'Income Category', 'Payment', 'Counter', 'Net', 'Discount', 'Tax', 'Total', 'Status', 'Date']
    : ['TXN #', 'SRB #', 'Member', 'Income Category', 'Payment', 'Counter', 'Net', 'Discount', 'Tax', 'Total', 'Status', 'Date'];

  // Base column widths (relative proportions)
  const baseColWidths = options.orientation === 'landscape'
    ? (isOwner
        ? [18, 28, 24, 20, 32, 26, 22, 18, 16, 14, 18, 16, 20]
        : [22, 32, 28, 38, 30, 24, 20, 18, 16, 20, 18, 22])
    : (isOwner
        ? [12, 18, 16, 14, 20, 18, 16, 12, 10, 10, 12, 10, 14]
        : [14, 20, 18, 22, 20, 16, 14, 12, 10, 14, 12, 16]);

  // Calculate available width (page width minus margins)
  const availableWidth = pageWidth - (margin * 2);
  const baseTableWidth = baseColWidths.reduce((sum, width) => sum + width, 0);
  
  // Scale column widths to use full available width proportionally
  const scaleFactor = availableWidth / baseTableWidth;
  const colWidths = baseColWidths.map(width => width * scaleFactor);
  const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
  
  // Center the table on the page for balanced appearance
  const tableStartX = (pageWidth - tableWidth) / 2;
  
  const headerFontSize = options.orientation === 'landscape' ? 8 : 6.5;
  const dataFontSize = options.orientation === 'landscape' ? 7.5 : 6;

  pdf.setFillColor(241, 245, 249);
  pdf.rect(tableStartX, yPosition, tableWidth, 8, 'F');
  pdf.setDrawColor(203, 213, 225);
  pdf.setLineWidth(0.5);
  pdf.line(tableStartX, yPosition + 8, tableStartX + tableWidth, yPosition + 8);

  pdf.setTextColor(51, 65, 85);
  pdf.setFontSize(headerFontSize);
  pdf.setFont('helvetica', 'bold');
  let xPosition = tableStartX;
  headers.forEach((header, index) => {
    const isNumericColumn = index >= (isOwner ? 7 : 6) && index <= (isOwner ? 10 : 9);
    const padding = 2;
    if (isNumericColumn) {
      pdf.text(header, xPosition + colWidths[index] - padding, yPosition + 5, { align: 'right' });
    } else {
      pdf.text(header, xPosition + padding, yPosition + 5);
    }
    xPosition += colWidths[index];
  });

  yPosition += 8;

  // Rows
  pdf.setFontSize(dataFontSize);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);
  const rowHeight = 7;
  let rowCount = 0;

  data.forEach((row) => {
    if (yPosition > pageHeight - 40) {
      pdf.addPage();
      yPosition = margin + 10;
      pdf.setFillColor(241, 245, 249);
      pdf.rect(tableStartX, yPosition, tableWidth, 8, 'F');
      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.5);
      pdf.line(tableStartX, yPosition + 8, tableStartX + tableWidth, yPosition + 8);
      pdf.setTextColor(51, 65, 85);
      pdf.setFontSize(headerFontSize);
      pdf.setFont('helvetica', 'bold');
      xPosition = tableStartX;
      headers.forEach((header, idx) => {
        const isNumericColumn = idx >= (isOwner ? 7 : 6) && idx <= (isOwner ? 10 : 9);
        const padding = 2;
        if (isNumericColumn) {
          pdf.text(header, xPosition + colWidths[idx] - padding, yPosition + 5, { align: 'right' });
        } else {
          pdf.text(header, xPosition + padding, yPosition + 5);
        }
        xPosition += colWidths[idx];
      });
      yPosition += 8;
      pdf.setFontSize(dataFontSize);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(15, 23, 42);
      rowCount = 0;
    }

    if (rowCount % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, tableWidth, rowHeight, 'F');
    }

    const uniqueCategories = Array.from(new Set(row.items?.map(item => item.income_category_name).filter(Boolean) || []));
    const categoryText = truncateText(uniqueCategories.join(', '), options.orientation === 'landscape' ? 30 : 20);
    const paymentMethods = Array.from(new Set(row.payments?.map(payment => payment.payment_method).filter(Boolean) || []));
    const paymentText = truncateText(paymentMethods.join(', '), options.orientation === 'landscape' ? 24 : 16);
    const maxTxnLength = options.orientation === 'landscape' ? 14 : 10;
    const maxMemberLength = options.orientation === 'landscape' ? 20 : 14;
    const maxCnicLength = options.orientation === 'landscape' ? 16 : 12;
    const maxSrbLength = options.orientation === 'landscape' ? 24 : 16;

    const rowData = isOwner
      ? [
          truncateText(row.receipt_number || '', maxTxnLength),
          truncateText(String(row.srb_invoice_id || ''), maxSrbLength),
          truncateText(row.member_name || '', maxMemberLength),
          truncateText(row.is_business ? (row.member_nic || '') : (formatNIC(row.member_nic || '') || ''), maxCnicLength),
          categoryText,
          paymentText,
          truncateText(row.counter_name || '', options.orientation === 'landscape' ? 18 : 14),
          formatCurrency(row.sub_total || 0),
          row.discount > 0 ? formatCurrency(row.discount) : '—',
          formatCurrency(row.tax_amount || 0),
          formatCurrency(row.total || 0),
          row.status || '',
          row.created_at ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : ''
        ]
      : [
          truncateText(row.receipt_number || '', maxTxnLength),
          truncateText(String(row.srb_invoice_id || ''), maxSrbLength),
          truncateText(row.member_name || '', maxMemberLength),
          categoryText,
          paymentText,
          truncateText(row.counter_name || '', options.orientation === 'landscape' ? 20 : 14),
          formatCurrency(row.sub_total || 0),
          row.discount > 0 ? formatCurrency(row.discount) : '—',
          formatCurrency(row.tax_amount || 0),
          formatCurrency(row.total || 0),
          row.status || '',
          row.created_at ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : ''
        ];

    xPosition = tableStartX;
    rowData.forEach((cellValue, cellIndex) => {
      const padding = 2;
      const isNumericColumn = cellIndex >= (isOwner ? 7 : 6) && cellIndex <= (isOwner ? 10 : 9);
      const isStatusColumn = cellIndex === (isOwner ? 11 : 10);
      if (cellIndex === 1) {
        pdf.setTextColor(100, 116, 139);
      } else if (isNumericColumn && cellIndex === (isOwner ? 8 : 7) && (row.discount || 0) > 0) {
        pdf.setTextColor(239, 68, 68);
      } else if (isNumericColumn) {
        pdf.setTextColor(15, 23, 42);
      } else if (isStatusColumn) {
        pdf.setTextColor(15, 23, 42);
      } else {
        pdf.setTextColor(71, 85, 105);
      }
      const textY = yPosition + 5;
      if (isNumericColumn) {
        pdf.text(cellValue.toString(), xPosition + colWidths[cellIndex] - padding, textY, { align: 'right' });
      } else {
        pdf.text(cellValue.toString(), xPosition + padding, textY);
      }
      xPosition += colWidths[cellIndex];
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + rowHeight, tableStartX + tableWidth, yPosition + rowHeight);
    yPosition += rowHeight;
    rowCount++;
  });

  const totalPages = pdf.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFillColor(249, 250, 251);
    pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.5);
    pdf.line(0, pageHeight - 15, pageWidth, pageHeight - 15);
    pdf.setTextColor(100, 116, 139);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('This is a computer-generated document. No signature is required.', pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text(`Report Period Summary | Total Records: ${data.length} of ${summary?.totalCount || data.length}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
    pdf.setTextColor(71, 85, 105);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  pdf.save(filename);
}


